import React, { useState, useEffect } from 'react';
import {
    List,
    Datagrid,
    TextField,
    BooleanField,
    FunctionField,
    Edit,
    Create,
    SimpleForm,
    TextInput,
    BooleanInput,
    ReferenceInput,
    AutocompleteInput,
    SaveButton,
    Toolbar,
    useNotify,
    useRedirect,
    useRecordContext,
} from 'react-admin';
import {
    Box, Typography, Button, Chip, Alert, Tooltip, FormControlLabel,
    FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemText,
    OutlinedInput, CircularProgress
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LinkIcon from '@mui/icons-material/Link';
import EmailIcon from '@mui/icons-material/Email';
import { stripedDataGridSx } from './styles/dataGridStyles';

const apiUrl = `${window.location.protocol}//${window.location.hostname}/api`;

const InviteStatusField = () => {
    const record = useRecordContext();
    if (!record) return null;
    if (record.has_password) return <Chip label="Active" size="small" color="success" />;
    if (record.invite_pending) return <Chip label="Invite Sent" size="small" color="warning" />;
    return <Chip label="Pending" size="small" color="default" />;
};

export const CustomerAccountList = () => (
    <List sort={{ field: 'created_at', order: 'DESC' }} perPage={25}>
        <Datagrid rowClick="edit" sx={stripedDataGridSx}>
            <TextField source="email" label="Email" />
            <TextField source="contact_name" label="Contact Name" />
            <TextField source="customer_name" label="Company" />
            <FunctionField label="Products" render={r => r.product_count || 0} />
            <FunctionField label="Locations" render={r => r.location_count || 0} />
            <InviteStatusField label="Status" />
            <BooleanField source="active" label="Active" />
        </Datagrid>
    </List>
);

const MultiSelectPicker = ({ label, items, selectedIds, onChange, loading, idField = 'id', labelField }) => {
    if (loading) return <CircularProgress size={24} />;

    return (
        <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{label}</InputLabel>
            <Select
                multiple
                value={selectedIds}
                onChange={e => onChange(e.target.value)}
                input={<OutlinedInput label={label} />}
                renderValue={(selected) => {
                    const names = selected.map(id => {
                        const item = items.find(i => i[idField] === id);
                        return item ? item[labelField] : id;
                    });
                    return names.join(', ');
                }}
                MenuProps={{ PaperProps: { style: { maxHeight: 400 } } }}
            >
                {items.map(item => (
                    <MenuItem key={item[idField]} value={item[idField]}>
                        <Checkbox checked={selectedIds.indexOf(item[idField]) > -1} />
                        <ListItemText primary={item[labelField]} />
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

const useProducts = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const token = localStorage.getItem('token');
        fetch(`${apiUrl}/products?_start=0&_end=1000&_sort=Product&_order=ASC`, { headers: { 'x-access-token': token } })
            .then(r => r.json())
            .then(setProducts)
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    return { products, loading };
};

const useLocationsByCustomer = (customerId) => {
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!customerId) {
            setLocations([]);
            return;
        }
        setLoading(true);
        const token = localStorage.getItem('token');
        fetch(`${apiUrl}/deliverylocations?_start=0&_end=1000&_sort=Delivery_Location&_order=ASC&cid=${customerId}`, { headers: { 'x-access-token': token } })
            .then(r => r.json())
            .then(locs => setLocations(locs.map(l => ({ ...l, display: `${l.Delivery_Location} (${l.City || ''})` }))))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [customerId]);

    return { locations, loading };
};

const AccountCreateForm = () => {
    const { products, loading: productsLoading } = useProducts();
    const [selectedCustomerId, setSelectedCustomerId] = useState(null);
    const { locations, loading: locationsLoading } = useLocationsByCustomer(selectedCustomerId);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [selectedLocations, setSelectedLocations] = useState([]);
    const [sendEmail, setSendEmail] = useState(false);

    const transform = (data) => ({
        ...data,
        product_ids: selectedProducts,
        location_ids: selectedLocations,
        send_email: sendEmail
    });

    const handleCustomerChange = (value) => {
        setSelectedCustomerId(value);
        setSelectedLocations([]);
    };

    return (
        <SimpleForm toolbar={
            <Toolbar>
                <SaveButton label={sendEmail ? "Create & Send Invite" : "Create Account"} icon={<SendIcon />} />
            </Toolbar>
        } transform={transform}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Account Info</Typography>
            <ReferenceInput source="customer_id" reference="customers" fullWidth={false} sx={{ minWidth: 300 }}>
                <AutocompleteInput label="Customer" optionText="Customer" onChange={handleCustomerChange} />
            </ReferenceInput>
            <TextInput source="email" label="Email" type="email" fullWidth required />
            <TextInput source="contact_name" label="Contact Name" fullWidth />

            <FormControlLabel
                control={<Checkbox checked={sendEmail} onChange={e => setSendEmail(e.target.checked)} />}
                label="Send invite email to customer"
                sx={{ mt: 1 }}
            />

            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Products Available to Customer</Typography>
            <MultiSelectPicker
                label="Products"
                items={products}
                selectedIds={selectedProducts}
                onChange={setSelectedProducts}
                loading={productsLoading}
                labelField="Product"
            />

            <Typography variant="subtitle1" sx={{ mt: 1, mb: 1 }}>Delivery Locations Available to Customer</Typography>
            {selectedCustomerId ? (
                <MultiSelectPicker
                    label="Delivery Locations"
                    items={locations}
                    selectedIds={selectedLocations}
                    onChange={setSelectedLocations}
                    loading={locationsLoading}
                    labelField="display"
                />
            ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Select a customer first to see their delivery locations.</Typography>
            )}
        </SimpleForm>
    );
};

const InviteLinkDisplay = ({ inviteToken, email, contactName, customerName }) => {
    const [copied, setCopied] = useState(false);

    if (!inviteToken) return null;

    const inviteUrl = `${window.location.protocol}//${window.location.hostname}/customer/setup?token=${inviteToken}`;

    const handleCopy = () => {
        navigator.clipboard.writeText(inviteUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const subject = encodeURIComponent('Welcome to the Pacific Landscape Supply Order Portal');
    const greeting = contactName ? `Hi ${contactName}` : 'Hi there';
    const body = encodeURIComponent(
        `${greeting},\n\nYou've been invited to the Pacific Landscape Supply online ordering portal for ${customerName || 'your company'}.\n\nClick the link below to set your password and get started:\n${inviteUrl}\n\nThis link expires in 7 days.\n\nIf you have any questions, please don't hesitate to reach out.\n\nThanks,\nPacific Landscape Supply`
    );
    const mailtoHref = `mailto:${email || ''}?subject=${subject}&body=${body}`;

    return (
        <Alert severity="info" icon={<LinkIcon />} sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Invite Link</Typography>
            <Typography variant="body2" sx={{ wordBreak: 'break-all', fontSize: '0.8rem', mb: 1 }}>{inviteUrl}</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title={copied ? 'Copied!' : 'Copy link'}>
                    <Button size="small" variant="outlined" startIcon={<ContentCopyIcon />} onClick={handleCopy}>
                        {copied ? 'Copied' : 'Copy'}
                    </Button>
                </Tooltip>
                <Button size="small" variant="outlined" startIcon={<EmailIcon />} href={mailtoHref}>
                    Email to Customer
                </Button>
            </Box>
        </Alert>
    );
};

export const CustomerAccountCreate = () => {
    const notify = useNotify();
    const redirect = useRedirect();

    return (
        <Create
            mutationOptions={{
                onSuccess: (data) => {
                    notify('Account created and invite sent');
                    redirect('edit', 'customeraccounts', data.id);
                }
            }}
        >
            <AccountCreateForm />
        </Create>
    );
};

const ResendInviteButton = () => {
    const record = useRecordContext();
    const notify = useNotify();
    const [sending, setSending] = useState(false);

    if (!record || record.has_password) return null;

    const handleResend = async () => {
        setSending(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${apiUrl}/customeraccounts/${record.id}/resend-invite`, {
                method: 'POST',
                headers: { 'x-access-token': token }
            });
            if (res.ok) {
                notify('Invite email sent');
            } else {
                const data = await res.json();
                notify(data.message || 'Failed to send invite', { type: 'error' });
            }
        } catch (e) {
            notify('Failed to send invite', { type: 'error' });
        }
        setSending(false);
    };

    return (
        <Button
            variant="outlined"
            startIcon={<SendIcon />}
            onClick={handleResend}
            disabled={sending}
            sx={{ mb: 2 }}
        >
            {sending ? 'Sending...' : 'Resend Invite'}
        </Button>
    );
};

const AccountEditForm = () => {
    const record = useRecordContext();
    const { products, loading: productsLoading } = useProducts();
    const { locations, loading: locationsLoading } = useLocationsByCustomer(record?.customer_id);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [selectedLocations, setSelectedLocations] = useState([]);
    const [initialized, setInitialized] = useState(false);

    useEffect(() => {
        if (record && !initialized) {
            setSelectedProducts(record.product_ids || []);
            setSelectedLocations(record.location_ids || []);
            setInitialized(true);
        }
    }, [record, initialized]);

    if (!record) return null;

    const transform = (data) => ({
        ...data,
        product_ids: selectedProducts,
        location_ids: selectedLocations
    });

    return (
        <SimpleForm transform={transform}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <InviteStatusField />
                <ResendInviteButton />
            </Box>

            {!record.has_password && record.invite_token && (
                <InviteLinkDisplay
                    inviteToken={record.invite_token}
                    email={record.email}
                    contactName={record.contact_name}
                    customerName={record.customer_name}
                />
            )}

            <Typography variant="subtitle1" sx={{ mb: 1 }}>Account Info</Typography>
            <TextInput source="email" label="Email" disabled fullWidth />
            <TextInput source="contact_name" label="Contact Name" fullWidth />
            <TextInput source="customer_name" label="Company" disabled fullWidth />
            <BooleanInput source="active" label="Active" />

            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Products Available to Customer</Typography>
            <MultiSelectPicker
                label="Products"
                items={products}
                selectedIds={selectedProducts}
                onChange={setSelectedProducts}
                loading={productsLoading}
                labelField="Product"
            />

            <Typography variant="subtitle1" sx={{ mt: 1, mb: 1 }}>Delivery Locations Available to Customer</Typography>
            <MultiSelectPicker
                label="Delivery Locations"
                items={locations}
                selectedIds={selectedLocations}
                onChange={setSelectedLocations}
                loading={locationsLoading}
                labelField="display"
            />
        </SimpleForm>
    );
};

export const CustomerAccountEdit = () => (
    <Edit>
        <AccountEditForm />
    </Edit>
);
